import { Injectable, inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { Role } from '../_models/role';
import { AuthenticationService } from '../_services/authentication.service';
import { AuthEventService } from '../_services/auth-event.service';

export interface RouteData {
  roles: Role[];
}

interface CustomActivatedRoute extends ActivatedRouteSnapshot {
  data: RouteData;
}

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  private readonly router = inject(Router);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly authEventService = inject(AuthEventService);

  canActivate(
    route: CustomActivatedRoute,
    state: RouterStateSnapshot,
  ): boolean | UrlTree {
    return this.checkRouteAccess(route, state);
  }

  private checkRouteAccess(
    route: CustomActivatedRoute,
    state: RouterStateSnapshot,
  ): boolean | UrlTree {
    const currentUser = this.authenticationService.currentUserValue;

    if (!currentUser) {
      void this.authenticationService.logout().catch(() => null);
      return this.router.createUrlTree(['/login'], {
        queryParams: { returnUrl: state.url },
      });
    }

    // A stale flag here would mean an unrelated 401 elsewhere silently
    // bypassed the unsaved-changes guard on whatever page loads next.
    this.authEventService.setUnauthorizedNavigationPending(false);

    if (currentUser.twoFactorSetupRequired) {
      return this.router.createUrlTree(['/2fa-enrollment']);
    }

    if (route.data.roles && route.data.roles.indexOf(currentUser.role) === -1) {
      return this.router.createUrlTree(['/403']);
    }

    return true;
  }
}
